import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../contexts/AuthContext';
import { newsletterService } from '../../../../services/newsletterService';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';

const AdminNewsletter = () => {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'active', 'inactive'

  useEffect(() => {
    fetchSubscribers();
  }, []);

  const fetchSubscribers = async () => {
    try {
      setLoading(true);
      const data = await newsletterService?.getAllSubscribers();
      setSubscribers(data);
    } catch (err) {
      console.error('Error fetching subscribers:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this subscriber?')) return;

    try {
      await newsletterService?.deleteSubscriber(id);
      setSubscribers(subscribers?.filter(sub => sub?.id !== id));
    } catch (err) {
      console.error('Error deleting subscriber:', err);
      alert('Failed to delete subscriber');
    }
  };

  const handleToggleStatus = async (subscriber) => {
    try {
      const newStatus = !subscriber?.isActive;
      await newsletterService?.toggleSubscriberStatus(subscriber?.id, newStatus);
      setSubscribers(subscribers?.map(sub => 
        sub?.id === subscriber?.id 
          ? { ...sub, isActive: newStatus, unsubscribedAt: newStatus ? null : new Date()?.toISOString() }
          : sub
      ));
    } catch (err) {
      console.error('Error toggling subscriber status:', err);
      alert('Failed to update subscriber status');
    }
  };

  const handleExport = async () => {
    try {
      const count = await newsletterService?.exportSubscribers();
      alert(`Successfully exported ${count} subscribers`);
    } catch (err) {
      console.error('Error exporting subscribers:', err);
      alert('Failed to export subscribers');
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/admin/login');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredSubscribers = subscribers?.filter(sub => {
    if (filter === 'active') return sub?.isActive;
    if (filter === 'inactive') return !sub?.isActive;
    return true;
  });

  const stats = {
    total: subscribers?.length || 0,
    active: subscribers?.filter(sub => sub?.isActive)?.length || 0,
    inactive: subscribers?.filter(sub => !sub?.isActive)?.length || 0
  };

  return (
    <>
      <Helmet>
        <title>Newsletter Management - Admin Dashboard</title>
      </Helmet>
      <div className="min-h-screen bg-muted/30">
        {/* Header */}
        <header className="bg-card border-b border-border sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Link to="/admin/dashboard">
                  <Button variant="ghost" size="sm" iconName="ArrowLeft" />
                </Link>
                <h1 className="text-lg md:text-xl font-bold text-foreground">Newsletter Management</h1>
              </div>
              <div className="flex items-center gap-2">
                <Link to="/">
                  <Button variant="outline" size="sm" iconName="Home" />
                </Link>
                <Button variant="ghost" size="sm" iconName="LogOut" onClick={handleSignOut} />
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-card rounded-xl border border-border p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Total Subscribers</p>
                  <p className="text-3xl font-bold text-foreground">{stats?.total}</p>
                </div>
                <Icon name="Users" size={32} className="text-accent" />
              </div>
            </div>
            <div className="bg-card rounded-xl border border-border p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Active</p>
                  <p className="text-3xl font-bold text-green-500">{stats?.active}</p>
                </div>
                <Icon name="CheckCircle" size={32} className="text-green-500" />
              </div>
            </div>
            <div className="bg-card rounded-xl border border-border p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Inactive</p>
                  <p className="text-3xl font-bold text-muted-foreground">{stats?.inactive}</p>
                </div>
                <Icon name="XCircle" size={32} className="text-muted-foreground" />
              </div>
            </div>
          </div>

          {/* Actions Bar */}
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
            <div>
              <h2 className="text-2xl font-bold text-foreground">Subscribers</h2>
              <p className="text-muted-foreground">Manage your newsletter subscribers</p>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                iconName="Download" 
                iconPosition="left"
                onClick={handleExport}
                disabled={subscribers?.length === 0}
              >
                Export CSV
              </Button>
            </div>
          </div>

          {/* Filter Tabs */}
          <div className="flex items-center gap-2 mb-6">
            <Button
              variant={filter === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('all')}
            >
              All ({stats?.total})
            </Button>
            <Button
              variant={filter === 'active' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('active')}
            >
              Active ({stats?.active})
            </Button>
            <Button
              variant={filter === 'inactive' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('inactive')}
            >
              Inactive ({stats?.inactive})
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading subscribers...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <Icon name="AlertCircle" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
              <p className="text-muted-foreground">{error}</p>
            </div>
          ) : filteredSubscribers?.length === 0 ? (
            <div className="text-center py-12 bg-card rounded-xl border border-border">
              <Icon name="Mail" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
              <p className="text-muted-foreground mb-2">No subscribers found</p>
              <p className="text-sm text-muted-foreground">Subscribers will appear here when users sign up for your newsletter</p>
            </div>
          ) : (
            <div className="bg-card rounded-xl border border-border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50 border-b border-border">
                    <tr>
                      <th className="text-left px-6 py-4 text-sm font-semibold text-foreground">Email</th>
                      <th className="text-left px-6 py-4 text-sm font-semibold text-foreground">Subscribed Date</th>
                      <th className="text-left px-6 py-4 text-sm font-semibold text-foreground">Status</th>
                      <th className="text-right px-6 py-4 text-sm font-semibold text-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filteredSubscribers?.map((subscriber) => (
                      <tr key={subscriber?.id} className="hover:bg-muted/30 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <Icon name="Mail" size={16} className="text-muted-foreground" />
                            <span className="text-sm text-foreground">{subscriber?.email}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-muted-foreground">
                            {formatDate(subscriber?.subscribedAt)}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                              subscriber?.isActive
                                ? 'bg-green-500/10 text-green-500' :'bg-muted text-muted-foreground'
                            }`}
                          >
                            <Icon 
                              name={subscriber?.isActive ? 'CheckCircle' : 'XCircle'} 
                              size={12} 
                            />
                            {subscriber?.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              iconName={subscriber?.isActive ? 'UserX' : 'UserCheck'}
                              onClick={() => handleToggleStatus(subscriber)}
                              title={subscriber?.isActive ? 'Deactivate' : 'Activate'}
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              iconName="Trash2"
                              onClick={() => handleDelete(subscriber?.id)}
                              title="Delete"
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </main>
      </div>
    </>
  );
};

export default AdminNewsletter;